import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Lock, Eye, EyeOff, CheckCircle, Loader2 } from "lucide-react";
import { authAPI } from "../../services/api";

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const validate = () => {
    if (password.length < 8) return "Password must be at least 8 characters.";
    if (password !== confirm) return "Passwords don't match.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate("/", { replace: true }), 3500);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "This reset link is invalid or has expired. Request a new one.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cyber-50 cyber-grid-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <a
            href="https://shinobi.milot.io/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Go to shinobi.milot.io"
            className="inline-block transition-transform hover:scale-105"
          >
            <img
              src="/logos/wasabi-shinobi-mark-256.png"
              alt="Wasabi Shinobi"
              className="w-20 h-20 mx-auto"
            />
          </a>
        </div>
        <div className="cyber-card rounded-lg p-8 shadow-2xl">
          {done ? (
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-neon-green/10 border border-neon-green/40 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-neon-green" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Password updated</h2>
            <p className="text-sm text-slate-500 mb-6">
              You can now log in with your new password.
            </p>
            <Link
              to="/"
              className="btn-cyber inline-block px-6 py-2 rounded font-semibold text-sm uppercase tracking-wider"
            >
              Go to login
            </Link>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold mb-2 text-slate-900">
                New Password
              </h1>
              <p className="text-sm text-gray-500">
                Choose a strong password, 8+ characters.
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded border border-red-200 bg-red-50 text-sm text-red-700">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="password"
                  className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2"
                >
                  New Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    id="password"
                    type={showPwd ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoFocus
                    className="terminal-input w-full pl-10 pr-10 py-3 rounded focus:ring-2 focus:ring-shinobi-purple"
                    placeholder="••••••••"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-slate-700"
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label
                  htmlFor="confirm"
                  className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2"
                >
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    id="confirm"
                    type={showPwd ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={8}
                    className="terminal-input w-full pl-10 pr-4 py-3 rounded focus:ring-2 focus:ring-shinobi-purple"
                    placeholder="••••••••"
                    disabled={loading}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-cyber w-full py-3 rounded font-semibold text-sm uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Updating…
                  </>
                ) : (
                  "Update Password"
                )}
              </button>
            </form>
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
